import { useContext, useEffect, useRef, useState } from 'react';
import AppContext from './../contexts/AppContext';
import DataContext from './../contexts/DataContext';
import Layout from './Layout';
import SlideContainer from './../components/SlideContainer';
import SlideProgress from '../components/SlideProgress';
import SlideProgressSunnyBot from '../components/SlideProgressSunnyBot';
import SlideProgressDetailed from '../components/SlideProgressDetailed';
import Copyright from '../components/Copyright';
import PopupMessage from '../components/PopupMessage';
import Button from './../components/Button';
import ConfigContext from '../contexts/ConfigContext';
import states from '../data/states';
import sunnyBotMessages from '../data/sunnyBotMessages';

import OptionsForProperty from '../components/OptionsForProperty';
import OptionsForSunlight from '../components/OptionsForSunlight';
import BillSlider from '../components/BillSlider';
import BillButtons from '../components/BillButtons';
import PropertyAddress from '../components/PropertyAddress';
import NeighborsMap from '../components/NeighborsMap';
import UtilityProvider from '../components/UtilityProvider';
import EmailInput from '../components/EmailInput';
import ContactInformation from '../components/ContactInformation';
import {
  LanderConceptFooter,
  LanderConceptHeader,
} from '../components/LanderConcept';
import QuestionContext from '../components/QuestionContext';
import QuestionContextV2 from '../components/QuestionContextV2';

import { UAParser } from 'ua-parser-js';
import SunnyBot, { SunnyBotBackground } from '../components/SunnyBot';

declare const Zuko: any;
declare const visitData: any;

export default function Home({ displayPage }: { displayPage?: boolean }) {
  const { features, testName } = useContext(ConfigContext);
  const {
    currentSlide,
    slides,
    showInitialPopup,
    closeInitialPopup,
    geoData,
    getSlideIndex,
    setLoadedHome,
    setStartedFunnel,
    zukoSlug,
    setSelectedNonFamilyHome,
    updateSelectedOption,
    updateCurrentSlide,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);

  const [isSingleFamilyHome, setIsSingleFamilyHome] = useState(true);

  useEffect(() => {
    const isSingleFamilyHome = !data.property
      ? true
      : data.property === 'Single Family Home'
      ? true
      : false;
    setIsSingleFamilyHome(isSingleFamilyHome);
    if (!isSingleFamilyHome) {
      setSelectedNonFamilyHome(data.property);
    }
  }, [data.property]);

  if (!isSingleFamilyHome) {
    closeInitialPopup();
  }

  const progress = ((currentSlide + 1) / (slides.length + 1)) * 100;

  const popupTitle = 'Power your home with sunshine.';
  const popupMessage =
    'Complete this form and a certified installer will contact you to discuss a solution for your exact energy needs.';

  const showPopup = showInitialPopup && currentSlide === 0;
  const titleRef: any = useRef(null);
  const slidesContainerRef: any = useRef(null);
  const slideTitle =
    features.useElecBillByState && slides[currentSlide].key === 'bill'
      ? `The average monthly electricity bill in ${data.selectedCity} is $${data.elecBillByState}. Is your bill higher or lower than this?`
      : slides[currentSlide].title;

  const getStateName = (stateCode: string): string | null => {
    for (const [key, value] of Object.entries(states)) {
      if (value === stateCode?.toUpperCase()) {
        return key;
      }
    }
    return null;
  };

  const getUtilityName = (): string => {
    const providerSlideIndex = getSlideIndex('provider');
    return slides[providerSlideIndex].selectedValue || data.selectedCity;
  };

  useEffect(() => {
    setStartedFunnel(true);
    setLoadedHome(true);
    let sourceData;
    try {
      sourceData = visitData.get();
    } catch (e: any) {
      sourceData = { source: 'direct', medium: 'none' };
    }

    const { device } = UAParser();
    Zuko.trackForm({
      target: document.querySelector('#root > div'),
      slug: zukoSlug || '',
    })
      .setAttribute('ab-test-variant', testName)
      .setAttribute('traffic-source', sourceData.source)
      .setAttribute('traffic-medium', sourceData.medium)
      .setAttribute(
        'device-model',
        !device.vendor && !device.model
          ? 'Unknown'
          : `${device.vendor ? device.vendor + ' ' : ''}${
              device.model ? device.model : ''
            }`
      )
      .trackEvent(Zuko.FORM_VIEW_EVENT);
  }, []);

  return (
    <div
      className={`${displayPage ? '' : 'hidden'} ${
        slides[currentSlide].key === 'bill' ? 'select-none' : ''
      }`}>
      <Layout>
        {features.showSunnyBot && <SunnyBotBackground />}
        {showPopup && (
          <PopupMessage
            title={popupTitle}
            message={popupMessage}
            onClose={() => closeInitialPopup()}
            className="md:hidden mt-10"
          />
        )}
        {isSingleFamilyHome && (
          <div
            className={`${
              features.showLanderConcept ? 'hidden' : 'md:hidden'
            } ${showPopup ? 'mt-[26px]' : 'mt-8'}`}>
            {features.useDetailedProgressBar ? (
              <SlideProgressDetailed percentage={progress} />
            ) : (
              <SlideProgress percentage={progress} />
            )}
          </div>
        )}
        <div
          className={`${
            features.showSunnyBot
              ? 'md:mt-[120px] mt-[115px]'
              : 'md:mt-[60px] mt-[26px]'
          } ${
            features.showQuestionsContextV2
              ? 'flex flex-col md:items-center'
              : ''
          }`}>
          {showPopup && (
            <PopupMessage
              title={popupTitle}
              message={popupMessage}
              onClose={() => closeInitialPopup()}
              className="md-max:hidden"
            />
          )}
          {features.showLanderConcept && <LanderConceptHeader />}
          <SlideContainer
            ref={slidesContainerRef}
            removeMinHeight={showPopup || !isSingleFamilyHome}
            hideMobileHeaderBackground={!isSingleFamilyHome}>
            {isSingleFamilyHome && (
              <>
                {features.showSunnyBot && (
                  <div className="hidden md:block">
                    <SunnyBot
                      data={sunnyBotMessages.find(
                        (message) => message.key === slides[currentSlide].key
                      )}></SunnyBot>
                    <SlideProgressSunnyBot percentage={progress} />
                  </div>
                )}

                <div
                  className={`${
                    features.showLanderConcept ? '' : 'md-max:hidden'
                  }`}>
                  {features.useDetailedProgressBar ? (
                    <div
                      className={
                        features.showLanderConcept
                          ? 'mt-[-20px] relative z-10'
                          : ''
                      }>
                      <SlideProgressDetailed percentage={progress} />
                    </div>
                  ) : (
                    <SlideProgress percentage={progress} />
                  )}
                </div>

                {features.useLocalizedHeadline &&
                  (data.selectedState || geoData.stateName) && (
                    <div>
                      <p className="text-[22px] md:text-[38px] md:mt-[40px] text-black font-bold leading-[1.2]">
                        {getStateName(data.selectedState) || geoData.stateName}{' '}
                        Homeowners Save Money with Solar.
                      </p>
                      <hr className="my-6 md:my-[30px]" />
                    </div>
                  )}

                {features.useUtilityHeadline && (
                  <div>
                    <p className="text-[22px] md:text-[38px] md:mt-[40px] text-black font-bold leading-[1.2]">
                      {getUtilityName()?.toLowerCase() !== 'not listed'
                        ? getUtilityName()
                        : ''}{' '}
                      Homeowners: See Your Cost Savings Now!
                    </p>
                    <hr className="my-6 md:my-[30px]" />
                  </div>
                )}

                {features.showSunnyBot && (
                  <div className="md:hidden">
                    <SunnyBot
                      data={sunnyBotMessages.find(
                        (message) => message.key === slides[currentSlide].key
                      )}></SunnyBot>
                    <SlideProgressSunnyBot percentage={progress} />
                  </div>
                )}

                <p
                  ref={titleRef}
                  className={`${
                    features.showSunnyBot
                      ? 'mt-8 md:mt-10 text-[18px] md:text-[26px] short:text-[18px]'
                      : 'md:mt-10 text-[24px] md:text-[32px]'
                  } text-black`}>
                  {slideTitle}
                </p>
                {features.showQuestionsContextV2
                  ? null
                  : features.showQuestionsContext &&
                    slides[currentSlide].context && (
                      <QuestionContext
                        title={slides[currentSlide].context.title}
                        content={slides[currentSlide].context.content}
                      />
                    )}
                <div className={showPopup ? `md:mb-[55px]` : ''}>
                  {slides[currentSlide].key === 'property' && (
                    <OptionsForProperty />
                  )}
                  {slides[currentSlide].key === 'sunlight' && (
                    <OptionsForSunlight />
                  )}
                  {slides[currentSlide].key === 'bill' && <BillSlider />}
                  {slides[currentSlide].key === 'billButtons' && (
                    <BillButtons />
                  )}
                  {slides[currentSlide].key === 'addressOwnership' && (
                    <PropertyAddress scrollToRef={slidesContainerRef} />
                  )}
                  {features.showNeighborsMap &&
                    slides[currentSlide].key === 'neighborsMap' && (
                      <NeighborsMap />
                    )}
                  {slides[currentSlide].key === 'provider' && (
                    <UtilityProvider />
                  )}
                  {slides[currentSlide].key === 'emailLink' && <EmailInput />}
                  {slides[currentSlide].key === 'contact' && (
                    <ContactInformation scrollToRef={titleRef} />
                  )}
                </div>
              </>
            )}
            {!isSingleFamilyHome && (
              <>
                <p className="text-[18px] md:text-[32px] mb-10 text-black w-full">
                  Sorry, we are unable to service you right now
                </p>
                <p className="mb-10 text-base">
                  Our estimator is only set up to provide preliminary estimates
                  and installer information to residents of single family homes.
                </p>
                <Button
                  title="Go back to form"
                  action={() => {
                    updateData({ ...data, property: null });
                    updateSelectedOption('property', 'null');
                    updateCurrentSlide(0);
                  }}
                  className="mb-4"
                />
              </>
            )}
          </SlideContainer>
          {features.showQuestionsContextV2 && slides[currentSlide].context && (
            <QuestionContextV2
              title={slides[currentSlide].context.title}
              content={slides[currentSlide].context.content}
            />
          )}
        </div>
        {features.showLanderConcept && <LanderConceptFooter />}
        <Copyright />
      </Layout>
    </div>
  );
}
